import Mirage, { Collection, _routeHandlersShorthandsGet as GetShorthandRouteHandler } from 'miragejs';
import config from 'garaje/config/environment';
import { assert } from '@ember/debug';
import { addDays, startOfMonth } from 'date-fns';
import { faker } from '@faker-js/faker';

export default function () {
  this.urlPrefix = config.deliveriesHost;

  // **************************************************************************
  // Carriers
  // **************************************************************************
  this.get('/a/deliveries/api/v2/carriers');

  // **************************************************************************
  // Deliveries
  // **************************************************************************
  this.get('/a/deliveries/api/v2/deliveries', function (schema, request) {
    // This is the equivalent of super, which preserves built in things like coalescing finds
    const handler = new GetShorthandRouteHandler(schema, this.serializerOrRegistry, 'delivery');
    const collection = handler.handleStringShorthand(request, schema.deliveries);

    const { queryParams } = request;
    const pageNumber = parseInt(queryParams['page[number]'], 10) || 1;
    const pageSize = parseInt(queryParams['page[size]'], 10) || 50;
    const start = pageNumber * pageSize - pageSize;
    const end = pageNumber * pageSize;
    const recordCount = collection.models.length;
    const pageCount = Math.ceil(recordCount / pageSize);
    const paginatedModels = collection.models.slice(start, end);
    const paginatedCollection = new Collection('delivery', paginatedModels);

    const response = this.serializerOrRegistry.serialize(paginatedCollection, request);

    response.meta = {
      'record-count': recordCount,
      'page-count': pageCount,
    };

    return new Mirage.Response(200, {}, response);
  });
  this.get('/a/deliveries/api/v2/deliveries/:id');
  this.patch('/a/deliveries/api/v2/deliveries/:id');
  this.delete('/a/deliveries/api/v2/deliveries/:id');
  this.delete('/a/deliveries/api/v2/deliveries/bulk', function (schema) {
    schema.deliveries.all().destroy();
    return new Mirage.Response(204, {}, '');
  });

  // **************************************************************************
  // Delivery areas
  // **************************************************************************
  this.get('/a/deliveries/api/v2/delivery-areas', function (schema, request) {
    const locationFilter = request.queryParams['filter[location]'];

    const records = schema.deliveryAreas.all();

    if (locationFilter) {
      const locationIds = locationFilter.split(',');
      return records.filter((deliveryArea) => {
        return locationIds.includes(deliveryArea.locationId);
      });
    }

    return records;
  });
  this.get('/a/deliveries/api/v2/delivery-areas/:id');
  this.patch('/a/deliveries/api/v2/delivery-areas/:id');
  this.post('/a/deliveries/api/v2/delivery-areas', function (schema, _request) {
    const attrs = this.normalizedRequestAttrs();

    // Mimic behavior of backend where it creates the subscription when the first delivery area is created
    let deliveriesSubscription = schema.subscriptions.findBy({ app: 'deliveries' });
    if (!deliveriesSubscription) {
      const locationId = attrs.locationId;
      const location = schema.locations.findBy({ id: locationId });
      const company = location.company;

      deliveriesSubscription = schema.subscriptions.create({
        companyId: company.id,
        plan: 'standard',
        isManual: false,
        app: 'deliveries',
        subscribed: false,
        onTrial: true,
        trialEndDate: addDays(new Date(), 8),
      });

      schema.db.companies.update(company.id, { deliveriesSubscriptionId: deliveriesSubscription.id });
    }

    const newDeliveryArea = schema.deliveryAreas.create(attrs);

    return newDeliveryArea;
  });

  // **************************************************************************
  // Delivery limit
  // **************************************************************************
  this.get('/a/deliveries/api/v2/delivery-limit', function (_schema, { _queryParams }) {
    const day = new Date();
    day.setMonth(day.getMonth() + 1);
    return {
      data: {
        id: '1',
        type: 'delivery-limit',
        attributes: {
          'enforce-limit': true,
          'limit-exceeded': false,
          'monthly-limit': 100,
          'monthly-limit-usage': 33,
          'resume-at': startOfMonth(day).toString(),
        },
      },
    };
  });

  // **************************************************************************
  // Delivery exports
  // **************************************************************************
  this.get('/a/deliveries/api/v2/delivery-exports/:id');
  this.post('/a/deliveries/api/v2/delivery-exports');

  // **************************************************************************
  // Delivery devices
  // **************************************************************************
  this.post('/a/deliveries/api/v2/devices', 'delivery-device');
  this.get('/a/deliveries/api/v2/devices', function (schema, request) {
    const deliveryAreaId = request.queryParams['filter[delivery-area]'];
    return schema.deliveryDevices.where({ deliveryAreaId });
  });
  this.get('/a/deliveries/api/v2/devices/:id', 'delivery-device');
  this.delete('/a/deliveries/api/v2/devices/:id', 'delivery-device');

  // **************************************************************************
  // Notification configs
  // **************************************************************************
  this.get('/a/deliveries/api/v2/notification-configs', function (schema, { queryParams }) {
    const locationId = queryParams['filter[location]'];
    assert(
      'You must filter /a/deliveries/api/v2/notification-configs by a single location id',
      locationId && locationId.split(',').length === 1
    );
    return schema.notificationConfigs.where({ locationId });
  });
  this.patch('/a/deliveries/api/v2/notification-configs/:id');

  // **************************************************************************
  // Notification schedules
  // **************************************************************************
  this.get('/a/deliveries/api/v2/notification-schedules/:id');
  this.patch('/a/deliveries/api/v2/notification-schedules/:id');

  // **************************************************************************
  // Recipient aliases
  // **************************************************************************
  this.get('/a/deliveries/api/v2/recipient-aliases');
  this.post('/a/deliveries/api/v2/recipient-aliases');
  this.delete('/a/deliveries/api/v2/recipient-aliases/:id');

  // **************************************************************************
  // Landing page
  // **************************************************************************
  this.get('/a/deliveries/api/v2/analytics/deliveries-landing-page/:location_id', function (_schema, _request) {
    return new Mirage.Response(
      200,
      {},
      {
        data: {
          'currently-waiting-for-pickup': faker.number.int({ min: 0, max: 84 }),
          'currently-waiting-for-pickup-change': faker.number.int({ min: -88, max: 260 }),
          'scanned-this-week': faker.number.int({ min: 0, max: 84 }),
          'scanned-this-week-change': faker.number.int({ min: -88, max: 260 }),
        },
      }
    );
  });
}
